import { stringify } from "query-string";
import React from "react";

import TextSearch from "@/mk2/apps/bazaar/components/TextSearch";
import SearchResults from "@/mk2/apps/blogs/components/SearchResults";
import { homepageUrl } from "@/mk2/apps/home/urls";
import ReviewArticlePreview, {
    ReviewArticlePreviewPlaceholder,
} from "@/mk2/apps/wiki/components/ReviewArticlePreview";
import { Props } from "@/mk2/apps/wiki/containers/ArticleSearch/ArticleSearchPage";
import { WIKI_REVIEWS_ARTICLE_SLUG } from "@/mk2/apps/wiki/helpers";
import { getWikiArticleUrl } from "@/mk2/apps/wiki/urls";
import { InfiniteListLoadable } from "@/mk2/components/InfiniteListLoadable";
import { Interpolate } from "@/mk2/components/Interpolate";
import { Failure } from "@/mk2/components/errors/Failure";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { LoadingState } from "@/mk2/reducers/loadingState";
import {
    BACK_TO_HOMEPAGE,
    WIKI_REVIEWS_SEARCH,
    WIKI_REVIEWS_SEARCH_PLACEHOLDER,
    WIKI_REVIEWS_SEARCH_RESULT_query_count,
} from "@/mk/autogenerated/translations/ArticleSearch.de5a11cee5030181196895b0bb3b56b6";

import styles from "./ArticleSearch.mscss";

const DEFAULT_ROW_HEIGHT = 80 /* height */ + 15; /* margin-bottom */

class ArticleSearch extends React.Component<Props> {
    public render() {
        const { loadingState, articles, searchQuery } = this.props;
        return (
            <div className={styles.ArticleSearch}>
                <TextSearch
                    value={searchQuery}
                    onChange={this.onSearchChange}
                    hideButtonUntilFocused
                    buttonLabel={WIKI_REVIEWS_SEARCH}
                    placeholder={WIKI_REVIEWS_SEARCH_PLACEHOLDER}
                />

                {!!searchQuery && (
                    <LoadingSwitch
                        loadingState={loadingState}
                        onLoad={this.load}
                        onRenderInit={this.renderInit}
                        onRenderSuccess={this.renderLoadSuccess}
                        onRenderFailure={this.renderFailure}
                        hasData={articles && articles.length > 0}
                    />
                )}
            </div>
        );
    }

    private renderFailure = () => {
        const { errorMessage } = this.props;

        return (
            <Failure
                errorMessage={errorMessage}
                onRetry={this.load}
                backButtonUrl={homepageUrl}
                backButtonLabel={BACK_TO_HOMEPAGE}
            />
        );
    };

    private renderInit = () => {
        return (
            <>
                {this.renderSearchResultsMeta()}
                <div className={styles.ArticleSearch__items}>
                    <ReviewArticlePreviewPlaceholder className={styles.ArticleSearch__item} />
                    <ReviewArticlePreviewPlaceholder className={styles.ArticleSearch__item} />
                    <ReviewArticlePreviewPlaceholder className={styles.ArticleSearch__item} />
                </div>
            </>
        );
    };

    private renderLoadSuccess = () => {
        const { hasNext, articles } = this.props;
        return (
            <div className={styles.ArticleSearch__items}>
                {this.renderSearchResultsMeta()}
                <div className={styles.ArticleSearch__items}>
                    <InfiniteListLoadable
                        isRowLoaded={this.isRowLoaded}
                        loadMoreRows={this.loadNext}
                        rowRenderer={(props) => this.rowRenderer(props)}
                        rowCount={hasNext ? articles.length + 1 : articles.length}
                        rowHeight={DEFAULT_ROW_HEIGHT}
                    />
                </div>
            </div>
        );
    };

    private onSearchChange = (value) => {
        const { routeNavigate, location } = this.props;
        if (value) {
            routeNavigate(
                {
                    ...location,
                    search: value
                        ? stringify({
                              q: value,
                          })
                        : "",
                },
                { replace: true },
            );
        } else {
            routeNavigate(getWikiArticleUrl(WIKI_REVIEWS_ARTICLE_SLUG, WIKI_REVIEWS_ARTICLE_SLUG), {
                replace: true,
            });
        }
    };

    private isRowLoaded = ({ index }) => !this.props.hasNext || index < this.props.articles.length;

    private rowRenderer = ({ index, key, style }) => {
        const { articles } = this.props;

        if (!this.isRowLoaded({ index })) {
            return (
                <ReviewArticlePreviewPlaceholder
                    key={key}
                    className={styles.ArticleSearch__item}
                    style={style}
                />
            );
        }

        const item = articles[index];
        return (
            <ReviewArticlePreview
                key={key}
                style={style}
                article={item}
                className={styles.ArticleSearch__item}
            />
        );
    };

    private load = () => {
        const { onLoad, categorySlug, articleSlug, after, searchQuery } = this.props;
        onLoad(categorySlug, articleSlug, searchQuery, after);
    };

    private loadNext = () => {
        // Do not trigger next load
        if (this.props.loadingState === LoadingState.LOADING) {
            return;
        }

        const { articles, articleSlug, categorySlug, onLoad, searchQuery } = this.props;

        let after = null;
        if (articles.length) {
            after = articles[articles.length - 1].id;
        }
        onLoad(categorySlug, articleSlug, searchQuery, after);

        // Debounce loadMoreRows
        return new Promise((resolve) => {
            setTimeout(resolve, 500);
        });
    };

    private renderSearchResultsMeta = () => {
        const { articles, loadingState, resultsCount, searchQuery } = this.props;
        return (
            <SearchResults
                loadingState={loadingState}
                hasData={Boolean(articles && articles.length > 0)}
                onCancelFilters={this.onCancelFilters}
            >
                <Interpolate
                    i18nKey={WIKI_REVIEWS_SEARCH_RESULT_query_count}
                    query={<strong>{searchQuery}</strong>}
                    count={resultsCount}
                />
            </SearchResults>
        );
    };

    private onCancelFilters = () => {
        this.onSearchChange(null);
    };
}

export default ArticleSearch;
